// Shared auto-RAG + target-parse helpers. Originally local to
// KpiLogPeriodEntry; extracted here so BulkPeriodEntryModal can reuse
// the exact same logic without copy-paste drift. Locked behaviour
// (contractually meaningful for QITC supplier scorecards):
//
// - Operator-aware green gate: `>=`, `<=`, `=` inclusive; `<`, `>`
//   strict (equality at boundary is a miss).
// - Amber band = `target * 0.05` (5% relative). Collapses to 0 for
//   non-positive targets so negative/zero targets don't get a
//   paradoxical "amber further away" read.
// - Rating-shaped targets ("4/5", "3 of 5") reject to null — user
//   must pick RAG manually because the denominator is implicit.
// - Boolean (Met / Not-met) KPIs skip the numeric branch
//   entirely: "met" → green, "not_met" → red. Amber never auto-
//   selects for boolean KPIs; callers must also block the amber
//   manual-override option.

import { type Kpi, type RagRating } from "../../types";
import { classifyBooleanKpiValue } from "./kpiValueParsers";

export interface TargetParse {
  numeric: number;
  operator: ">=" | "<=" | ">" | "<" | "=" | null;
  suffix: string; // e.g. "%", "hrs", ""
}

const UNIT_RE = /%|\b(?:hrs?|hours|ms|minutes|sec|seconds)\b/iu;
const TARGET_RE = /(>=|<=|≥|≤|>|<|=)?\s*(-?\d+(?:\.\d+)?)/u;
// Rating-shaped: two numbers separated by a non-comparator character.
// Mirrors parseKpiMeasurementValue.ts's multi-number guard.
const MULTI_NUMBER_RE = /\d[^0-9.\-%<>≤≥=\s]+\d/u;

export const AMBER_BAND_RELATIVE = 0.05;

export function parseTarget(target: string): TargetParse | null {
  if (!target) return null;
  if (MULTI_NUMBER_RE.test(target)) return null;
  const suffixMatch = UNIT_RE.exec(target);
  const suffix = suffixMatch ? suffixMatch[0] : "";
  const m = TARGET_RE.exec(target);
  if (!m) return null;
  const rawOp = m[1];
  const n = Number(m[2]);
  if (!Number.isFinite(n)) return null;
  let operator: TargetParse["operator"] = null;
  if (rawOp === ">=" || rawOp === "≥") operator = ">=";
  else if (rawOp === "<=" || rawOp === "≤") operator = "<=";
  else if (rawOp === ">") operator = ">";
  else if (rawOp === "<") operator = "<";
  else if (rawOp === "=") operator = "=";
  return { numeric: n, operator, suffix };
}

export function parseValue(raw: string): number | null {
  if (!raw) return null;
  const stripped = raw.trim().replace(/%$/u, "").trim();
  if (!stripped) return null;
  const n = Number(stripped);
  return Number.isFinite(n) ? n : null;
}

export function computeAutoRag(value: string, kpi: Kpi): RagRating | null {
  // Boolean branch. Met → green, Not-met → red. No amber
  // band, no higherIsBetter comparison; direction flag is meaningless
  // for Met/Not-met. Value is a literal string (KPI_BOOLEAN_MET /
  // KPI_BOOLEAN_NOT_MET); anything else returns null so the modal
  // knows to leave the radios unset. The amber manual-override must
  // also be gated at the callsite — auto-RAG alone can't stop a
  // user clicking it.
  if (kpi.valueType === "boolean") {
    const cls = classifyBooleanKpiValue(value);
    if (cls === "met") return "green";
    if (cls === "not-met") return "red";
    return null;
  }
  if (typeof kpi.higherIsBetter !== "boolean") return null;
  const n = parseValue(value);
  if (n === null) return null;
  const parsed = parseTarget(kpi.target);
  if (!parsed) return null;
  const target = parsed.numeric;
  // Per-KPI amber-band override. Backend Zod guard guarantees
  // `0 < amberBandPercent <= 0.5` when set. Null / undefined falls back
  // to the locked default so legacy KPIs behave exactly as before.
  const bandRatio =
    typeof kpi.amberBandPercent === "number"
      ? kpi.amberBandPercent
      : AMBER_BAND_RELATIVE;
  const band = target > 0 ? target * bandRatio : 0;

  let meetsTarget: boolean;
  switch (parsed.operator) {
    case ">=":
      meetsTarget = n >= target;
      break;
    case "<=":
      meetsTarget = n <= target;
      break;
    case ">":
      meetsTarget = n > target;
      break;
    case "<":
      meetsTarget = n < target;
      break;
    case "=":
      meetsTarget = n === target;
      break;
    default:
      meetsTarget = kpi.higherIsBetter ? n >= target : n <= target;
  }
  if (meetsTarget) return "green";

  if (kpi.higherIsBetter) {
    return n >= target - band ? "amber" : "red";
  }
  return n <= target + band ? "amber" : "red";
}
